import {
  WidgetConfig,
  WidgetType,
} from '../services/grid-widget-config.service';
import { WidgetHelperService } from '../services/widget/widget-helper.service';
import { updateAlpha } from './color.utility';
import { BaseDataset } from '../stores/grid/chart.types';

/**
 * Applies styling to datasets based on selected and highlighted features.
 * This should be called AFTER sorting to ensure styling is applied to correct indices.
 *
 * @param {any[]} datasets - The array of datasets to apply styling to.
 * @param {WidgetConfig} config - Configuration settings for the widget.
 * @param {WidgetType} type - The widget type (e.g., chart type).
 * @param {string[]} sortedKeys - The sorted keys (from groups after sorting).
 * @param {Array<{ key: string; type: string }> | null} selectedFeatures - Selected features to highlight.
 * @param {{ key: string; type: string } | undefined} highlightedFeature - The highlighted feature.
 * @param {string} dsProperty - The dataset property type.
 * @param {WidgetHelperService} helper - Widget helper service for color modifications.
 *
 * @returns {any[]} The datasets with styling applied.
 */
export function applyDatasetStyling(
  datasets: BaseDataset[],
  config: WidgetConfig,
  type: WidgetType,
  sortedKeys: string[],
  selectedFeatures: Array<{ key: string; type: string }> | null,
  highlightedFeature: { key: string; type: string } | undefined,
  dsProperty: string,
  helper: WidgetHelperService
): any[] {
  return datasets.map(dataset => {
    const dataLength = dataset.data.length;
    let borderColor = dataset.borderColor;
    let borderWidth: number | number[] = Array(dataLength).fill(
      config.borderWidth || 2
    );
    let borderDash: number[] | undefined = undefined;

    // Handle highlighted feature/indicator/dataLayer
    if (highlightedFeature) {
      borderColor = updateAlpha(dataset.backgroundColor, 1);

      // Determine which dimension to highlight based on the type
      if (highlightedFeature.type === config.groupProperty) {
        // Highlighting affects the x-axis (groups) - modify specific bar/point in each dataset
        const highlightIndex = sortedKeys.indexOf(highlightedFeature.key);
        if (highlightIndex !== -1) {
          borderWidth[highlightIndex] = 4;
        }
      } else if (highlightedFeature.type === dsProperty) {
        // Highlighting affects the datasets themselves - make entire dataset bold
        if ((dataset as any).labelKey === highlightedFeature.key) {
          borderWidth = Array(dataLength).fill(4);
        }
      }
    }

    // Handle selected features/indicators/dataLayers
    if (selectedFeatures && selectedFeatures.length) {
      // Group selected items by type
      const selectedByType = {
        onGroups: selectedFeatures
          .filter(item => item.type === config.groupProperty)
          .map(item => item.key),
        onDatasets: selectedFeatures
          .filter(item => item.type === dsProperty)
          .map(item => item.key),
      };

      // Apply selection styling based on where the selections are
      if (selectedByType.onGroups.length > 0) {
        // Selections on x-axis (groups) - modify specific bars/points
        const selectedIndices = selectedByType.onGroups
          .map(key => sortedKeys.indexOf(key))
          .filter(idx => idx !== -1);

        if (selectedIndices.length > 0) {
          // For line charts, keep the original color but use dashed lines
          if (type === 'line') {
            borderColor = dataset.backgroundColor;
            borderDash = [5, 3];
            borderWidth = Array(dataLength).fill((config.borderWidth || 2) + 1);
          } else {
            borderColor = helper.modifyChartColors(
              dataset.backgroundColor,
              selectedIndices,
              {
                color: '#000000',
                opacity: config.featureOpacity
                  ? config.featureOpacity + 0.4
                  : 1,
              },
              dataLength
            );
          }
        }
      }

      if (selectedByType.onDatasets.length > 0) {
        // Selections on legend (datasets) - make entire dataset bold
        if (selectedByType.onDatasets.includes((dataset as any).labelKey)) {
          // For line charts, use dashed line instead of black color
          if (type === 'line') {
            borderColor = dataset.backgroundColor;
            borderDash = [5, 3];
            borderWidth = Array(dataLength).fill((config.borderWidth || 2) + 1);
          } else {
            borderColor = '#444444';
            if (!Array.isArray(borderWidth) || borderWidth[0] !== 4) {
              borderWidth = Array(dataLength).fill(3);
            }
          }
        }
      }

      // Fallback: If no specific styling applied, use default enhanced opacity
      if (!borderColor) {
        borderColor = updateAlpha(
          dataset.backgroundColor,
          config.featureOpacity ? config.featureOpacity + 0.4 : 1
        );
      }
    } else if (!highlightedFeature) {
      borderColor = updateAlpha(
        dataset.backgroundColor,
        config.featureOpacity ? config.featureOpacity + 0.4 : 1
      );
    }

    return {
      ...dataset,
      borderColor,
      borderWidth,
      borderDash,
    };
  });
}
